import { useState, useEffect, useCallback } from 'react';
import api from '../../hooks/useApi';
import { formatMoney } from '../../utils/format';
import { useToast } from '../ui/Toast';

// The project cost view on a SOW's Costs tab (OC Bizops tracker item 5).
//
// One bold row per milestone with Labour and Materials rows beneath it, each
// milestone collapsible so a project with many milestones stays readable.
// Settled milestones start collapsed, live ones open. Budget, incurred and the
// figures behind them come from GET /sows/:id/cost-summary; cost to complete
// is the one thing typed in here, by whoever the server says may forecast,
// and is saved through the ordinary milestone PUT so it lands in the
// milestone's history like any other change.

export const SETTLED_STATUSES = ['Paid', 'Invoiced'];
const FIELD_OF = { labour: 'labour_cost_to_complete', materials: 'materials_cost_to_complete' };

const variance = (budget, incurred, toComplete) => (budget || 0) - ((incurred || 0) + (toComplete || 0));

function VarianceCell({ value, bold }) {
  const cls = value > 0.005 ? 'text-emerald-700 bg-emerald-50' : value < -0.005 ? 'text-red-700 bg-red-50' : 'text-gray-400';
  return <td className={`px-2 py-1 text-[10px] text-right font-bold ${cls} ${bold ? 'text-[10.5px]' : ''}`}>{formatMoney(value, 2)}</td>;
}

function Money({ value, bold, muted }) {
  return <td className={`px-2 py-1 text-[10px] text-right ${bold ? 'font-bold text-navy' : ''} ${muted ? 'text-gray-500' : ''}`}>{formatMoney(value || 0, 2)}</td>;
}

export default function ProjectCostTable({ sowId, refreshKey = 0, onChanged }) {
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [collapsed, setCollapsed] = useState(null); // Set of milestone ids; null until first load
  const [editing, setEditing] = useState(null);     // { id, field }
  const [tempVal, setTempVal] = useState('');
  const [saveError, setSaveError] = useState(null); // { id, field, message }

  const load = useCallback(async () => {
    if (!sowId) return;
    try {
      const r = await api.get(`sows/${sowId}/cost-summary`);
      setData(r);
      setError(null);
      // Collapse the settled milestones once, on first load, and leave the
      // person's choices alone on every refresh after that.
      setCollapsed(prev => prev ?? new Set((r.milestones || []).filter(m => SETTLED_STATUSES.includes(m.status)).map(m => m.id)));
    } catch (e) {
      setError(e.message || 'Could not load the project cost view.');
    }
  }, [sowId]);
  useEffect(() => { load(); }, [load, refreshKey]);

  if (error) return <div className="text-[10px] text-red-600 mb-3">{error}</div>;
  if (!data) return <div className="text-[10px] text-gray-400 mb-3">Loading project costs…</div>;

  const { milestones = [], unallocated = {}, totals = {}, can_edit_forecast: canEdit } = data;
  const isCollapsed = (id) => collapsed?.has(id);
  const toggle = (id) => setCollapsed(prev => { const n = new Set(prev || []); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const setAll = (open) => setCollapsed(open ? new Set() : new Set(milestones.map(m => m.id)));

  const startEdit = (m, kind) => {
    if (!canEdit) return;
    const field = FIELD_OF[kind];
    setEditing({ id: m.id, field });
    setTempVal(m[field] == null ? '' : String(m[field]));
    setSaveError(null);
  };
  const cancelEdit = () => { setEditing(null); setTempVal(''); };
  const commitEdit = async (m) => {
    if (!editing) return;
    const { id, field } = editing;
    const next = tempVal.trim() === '' ? null : Number(tempVal);
    const current = m[field] == null ? null : Number(m[field]);
    setEditing(null);
    if (next === current) return;
    if (next !== null && (!Number.isFinite(next) || next < 0)) {
      setSaveError({ id, field, message: 'Enter a positive amount, or clear it.' });
      return;
    }
    try {
      await api.put(`milestones/${id}`, { [field]: next });
      await load();
      onChanged?.();
    } catch (e) {
      setSaveError({ id, field, message: e.message || 'Could not save the cost to complete.' });
      toast(e.message || 'Could not save the cost to complete.', 'error');
    }
  };

  const toCompleteCell = (m, kind) => {
    const field = FIELD_OF[kind];
    const val = m[field];
    if (editing && editing.id === m.id && editing.field === field) {
      return (
        <td className="px-2 py-0.5 text-right">
          <input
            type="number" min="0" step="0.01" autoFocus
            className="w-24 text-right border border-gold rounded px-1 py-0.5 text-[10px] bg-white"
            value={tempVal}
            onChange={e => setTempVal(e.target.value)}
            onBlur={() => commitEdit(m)}
            onKeyDown={e => { if (e.key === 'Enter') commitEdit(m); if (e.key === 'Escape') cancelEdit(); }}
          />
        </td>
      );
    }
    const err = saveError && saveError.id === m.id && saveError.field === field ? saveError.message : null;
    return (
      <td className="px-2 py-1 text-[10px] text-right">
        <span
          className={`${canEdit ? 'cursor-text border-b border-dotted border-gray-400 hover:border-gold hover:text-navy' : ''} ${val == null ? 'text-gray-400' : ''}`}
          title={canEdit ? 'Click to enter the forecast cost to complete' : undefined}
          onClick={() => startEdit(m, kind)}
        >{val == null ? (canEdit ? 'Enter…' : '—') : formatMoney(val, 2)}</span>
        {err && <div className="text-[8px] text-red-600 mt-0.5 whitespace-nowrap">{err}</div>}
      </td>
    );
  };

  const msTotals = (m) => ({
    incurred: (m.labour_incurred || 0) + (m.materials_incurred || 0),
    toComplete: (m.labour_cost_to_complete || 0) + (m.materials_cost_to_complete || 0),
  });

  const hasUnallocated = (unallocated.materials_budget || 0) !== 0 || (unallocated.labour_incurred || 0) !== 0 || (unallocated.materials_incurred || 0) !== 0;
  const totalIncurred = (totals.labour_incurred || 0) + (totals.materials_incurred || 0);
  const totalToComplete = (totals.labour_cost_to_complete || 0) + (totals.materials_cost_to_complete || 0);
  const anyForecast = milestones.some(m => m.labour_cost_to_complete != null || m.materials_cost_to_complete != null);

  const subRow = (label, kind, m) => (
    <tr key={`${m.id}-${kind}`} className="border-b border-gray-100 bg-gray-50/60">
      <td className="px-2 py-1" />
      <td className="px-2 py-1" />
      <td className="px-2 py-1">
        <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded ${kind === 'labour' ? 'bg-blue-50 text-blue-700' : 'bg-violet-50 text-violet-700'}`}>{label}</span>
      </td>
      <Money value={m[`${kind}_budget`]} muted />
      <Money value={m[`${kind}_incurred`]} muted />
      {toCompleteCell(m, kind)}
      <VarianceCell value={variance(m[`${kind}_budget`], m[`${kind}_incurred`], m[FIELD_OF[kind]])} />
    </tr>
  );

  return (
    <div className="mb-4" data-tour="project-cost-table">
      <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
        <div className="text-[9px] text-gray-500">
          Budget is the milestone amount, with materials as the sell value of the hardware lines assigned to it and labour the rest.
          Incurred is time booked to the milestone and raised purchase orders for its hardware.
          {canEdit ? ' Click a "to complete" figure to enter the forecast.' : ''}
        </div>
        {milestones.length > 0 && (
          <div className="inline-flex border border-gray-200 rounded overflow-hidden text-[9px] font-semibold">
            <button type="button" onClick={() => setAll(true)} className="px-2 py-1 text-gray-600 hover:bg-gray-50">Expand all</button>
            <button type="button" onClick={() => setAll(false)} className="px-2 py-1 text-gray-600 hover:bg-gray-50 border-l border-gray-200">Collapse all</button>
          </div>
        )}
      </div>
      <div className="bg-white border border-gray-200 rounded overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50">
              {[['Milestone', 'text-left'], ['Status', 'text-left'], ['Description', 'text-left'], ['Budget', 'text-right'], ['Incurred', 'text-right'], ['To complete', 'text-right'], ['Variance', 'text-right']].map(([h, align]) => (
                <th key={h} className={`px-2 py-1 text-[8px] uppercase text-gray-500 font-bold border-b ${align}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {milestones.map(m => {
              const t = msTotals(m);
              const open = !isCollapsed(m.id);
              return [
                <tr key={m.id} className="border-b border-gray-100 hover:bg-gold/5 cursor-pointer" onClick={() => toggle(m.id)} title={open ? 'Collapse' : 'Expand'}>
                  <td className="px-2 py-1 text-[10px] font-bold text-navy whitespace-nowrap">
                    <span className="inline-block w-3 text-gray-400 text-[9px]">{open ? '▾' : '▸'}</span>{m.milestone_code}
                  </td>
                  <td className="px-2 py-1 text-[9px]">{m.status}</td>
                  <td className="px-2 py-1 text-[9px] text-gray-600 max-w-[260px] truncate">{m.description || '—'}</td>
                  <Money value={m.budget} bold />
                  <Money value={t.incurred} bold />
                  <Money value={t.toComplete} bold />
                  <VarianceCell value={variance(m.budget, t.incurred, t.toComplete)} bold />
                </tr>,
                ...(open ? [subRow('Labour', 'labour', m), subRow('Materials', 'materials', m)] : []),
              ];
            })}
            {hasUnallocated && (
              <tr className="border-b border-gray-100 bg-amber-50/40">
                <td className="px-2 py-1 text-[9px] font-semibold text-amber-800" colSpan={3}>
                  Unallocated
                  <div className="text-[8px] font-normal text-amber-700">Time with no milestone, hardware not assigned to one, and purchase order lines not tied to a hardware line.</div>
                </td>
                <td className="px-2 py-1 text-[9px] text-right text-gray-500">{unallocated.materials_budget ? `${formatMoney(unallocated.materials_budget, 2)} hardware` : '—'}</td>
                <Money value={(unallocated.labour_incurred || 0) + (unallocated.materials_incurred || 0)} />
                <td className="px-2 py-1 text-[9px] text-right text-gray-400">—</td>
                <td className="px-2 py-1 text-[9px] text-right text-gray-400">—</td>
              </tr>
            )}
            {milestones.length === 0 && (
              <tr><td colSpan="7" className="text-center py-6 text-[10px] text-gray-400">No live milestones on this SOW yet.</td></tr>
            )}
          </tbody>
          {milestones.length > 0 && (
            <tfoot>
              <tr className="bg-gray-50 border-t-2 border-gray-200">
                <td colSpan={3} className="px-2 py-1 text-[9px] font-bold text-gray-600 text-right">Project total</td>
                <Money value={totals.budget} bold />
                <Money value={totalIncurred} bold />
                <Money value={totalToComplete} bold />
                <VarianceCell value={variance(totals.budget, totalIncurred, totalToComplete)} bold />
              </tr>
              <tr className="bg-gray-50">
                <td colSpan={3} className="px-2 py-0.5 text-[8.5px] text-gray-500 text-right">of which Labour</td>
                <Money value={totals.labour_budget} muted />
                <Money value={totals.labour_incurred} muted />
                <Money value={totals.labour_cost_to_complete} muted />
                <VarianceCell value={variance(totals.labour_budget, totals.labour_incurred, totals.labour_cost_to_complete)} />
              </tr>
              <tr className="bg-gray-50">
                <td colSpan={3} className="px-2 py-0.5 text-[8.5px] text-gray-500 text-right">of which Materials</td>
                <Money value={totals.materials_budget} muted />
                <Money value={totals.materials_incurred} muted />
                <Money value={totals.materials_cost_to_complete} muted />
                <VarianceCell value={variance(totals.materials_budget, totals.materials_incurred, totals.materials_cost_to_complete)} />
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      {milestones.length > 0 && !anyForecast && (
        <div className="text-[8.5px] text-gray-400 mt-1">No cost to complete has been entered yet, so variance is budget minus incurred only.</div>
      )}
    </div>
  );
}
